<script setup>
import { computed, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { picoSearch } from '@chatwoot/pico-search';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import {
  resolveVariablesInMessage,
  stripUnsupportedFormatting,
} from 'dashboard/helper/editorHelper';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import CaretAnchoredPicker from 'dashboard/components-next/preview-picker/CaretAnchoredPicker.vue';

const props = defineProps({
  caretPosition: {
    type: Object,
    default: null,
  },
  searchKey: {
    type: String,
    default: '',
  },
  variables: {
    type: Object,
    default: () => ({}),
  },
  schema: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['replace', 'close', 'removeTrigger']);

// Characters kept before the match when a snippet has to skip ahead
const SNIPPET_LEAD = 24;
const HIGHLIGHT_CLASS = 'text-n-blue-text';

const store = useStore();
const { t } = useI18n();
const { getPlainText, formatMessage, highlightContent } = useMessageFormatter();

const cannedResponses = useMapGetter('getCannedResponses');
const uiFlags = useMapGetter('getUIFlags');
// The trigger can already be followed by text, from a draft or a caret moved back onto it
const searchQuery = ref(props.searchKey);

const searchTerm = computed(() => searchQuery.value.trim());

// An empty term makes `highlightContent`'s regex match at every position, wrapping the
// whole string in empty spans
const highlightMatches = text =>
  searchTerm.value
    ? highlightContent(text, searchTerm.value, HIGHLIGHT_CLASS)
    : text;

const buildSnippet = text => {
  const term = searchTerm.value;
  if (!term) return text;

  const index = text.toLowerCase().indexOf(term.toLowerCase());
  if (index <= SNIPPET_LEAD) return text;

  return `…${text.slice(index - SNIPPET_LEAD)}`;
};

// Both steps mirror what insertion does: variables are substituted, then formatting the
// channel's schema cannot carry is stripped. Previewing the raw content would advertise
// styling the message never ends up with.
const resolveContent = message =>
  stripUnsupportedFormatting(
    resolveVariablesInMessage(message, props.variables),
    props.schema
  );

const records = computed(() =>
  cannedResponses.value.map(
    ({ id, short_code: shortCode, content, attachments = [] }) => {
      const resolved = resolveContent(content);
      return {
        id,
        content,
        // [PROSPEK] 2026-06-11 media quick replies - preserve attachment metadata for selection.
        attachments,
        // [/PROSPEK]
        resolved,
        shortCode,
        plainText: getPlainText(resolved).replace(/\s+/g, ' ').trim(),
      };
    }
  )
);

const filteredRecords = computed(() => {
  if (!searchTerm.value) return records.value;

  return picoSearch(records.value, searchTerm.value, [
    { name: 'shortCode', weight: 1 },
    'plainText',
  ]);
});

const items = computed(() =>
  filteredRecords.value.map(record => ({
    id: record.id,
    content: record.content,
    // [PROSPEK] 2026-06-11 media quick replies - preserve attachment metadata for selection.
    attachments: record.attachments,
    shortCode: record.shortCode,
    // [/PROSPEK]
    resolved: record.resolved,
    label: `/${record.shortCode}`,
    title: highlightMatches(`/${record.shortCode}`),
    subtitle: highlightMatches(buildSnippet(record.plainText)),
  }))
);

// [PROSPEK] 2026-06-11 media quick replies - emit the full response object.
const onSelect = item =>
  emit('replace', {
    id: item.id,
    short_code: item.shortCode,
    content: item.content,
    attachments: item.attachments,
  });
// [/PROSPEK]

onMounted(() => store.dispatch('getCannedResponse'));
</script>

<template>
  <CaretAnchoredPicker
    v-model:search="searchQuery"
    :caret-position="caretPosition"
    :items="items"
    :search-placeholder="t('COMBOBOX.SEARCH_PLACEHOLDER')"
    :is-loading="uiFlags.fetchingList"
    :empty-label="
      searchTerm
        ? t('COMBOBOX.EMPTY_SEARCH_RESULTS', { searchTerm })
        : t('COMBOBOX.EMPTY_STATE')
    "
    @select="onSelect"
    @close="emit('close')"
    @remove-trigger="emit('removeTrigger')"
  >
    <template #preview="{ item }">
      <div
        v-dompurify-html="formatMessage(item?.resolved || '')"
        class="px-4 py-3 prose prose-bubble !max-w-none prose-a:text-n-brand"
      />
      <!-- [PROSPEK] 2026-06-11 media quick replies - show attached media in the preview. -->
      <div
        v-if="item?.attachments?.length"
        class="flex items-center gap-1 px-4 pb-3"
      >
        <span
          v-for="attachment in item.attachments.slice(0, 3)"
          :key="attachment.id"
          class="flex items-center justify-center w-6 h-6 overflow-hidden rounded bg-n-slate-3 text-xs text-n-slate-11"
        >
          <img
            v-if="attachment.content_type?.startsWith('image/')"
            :src="attachment.preview_url"
            class="object-cover w-full h-full"
          />
          <span v-else class="i-lucide-file w-4 h-4" />
        </span>
        <span
          v-if="item.attachments.length > 3"
          class="text-xs text-n-slate-11"
        >
          +{{ item.attachments.length - 3 }}
        </span>
      </div>
      <!-- [/PROSPEK] -->
    </template>
  </CaretAnchoredPicker>
</template>
